import { LtIcons } from "@landtechnologies/icons/font/lt-icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { clsx } from "clsx";
import { useRef, useState } from "react";
import { Icon } from "react-migration/components/Icon";
import { Navbar } from "react-migration/domains/nav/components/Navbar";
import { useActiveNavTab } from "react-migration/layouts/nav/ActiveNavTabContext";
import { AnimatedSection } from "react-migration/layouts/nav/components/AnimatedSection";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import Feature from "src/js/stores/user/Feature";
import { NavbarDropdownMenuCloseProvider, useNavbarDropdownMenuClose } from "./NavbarDropdownMenuCloseContext";

type NavbarItem = {
  id: string;
  icon: `${LtIcons}`;
  label: string;
  subLabel: string;
  onClick: () => void;
};

const NavbarDropdownMenuItem = ({ item }: { item: NavbarItem }) => {
  const closeNavbarDropdownMenu = useNavbarDropdownMenuClose();
  return (
    <DropdownMenu.Item
      className={clsx(
        "relative flex items-start gap-2 px-1.5 py-1 my-0.5 mx-1.5 text-sm select-none outline-hidden rounded-md",
        "cursor-pointer"
      )}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        item.onClick();
        closeNavbarDropdownMenu();
      }}
    >
      <div className="flex items-center gap-2">
        <Icon icon={`icon-${item.icon}`} size="lg" />
        <div className="flex flex-col">
          <span className="text-sm">{item.label}</span>
          {item.subLabel && <span className="text-xs text-text-base-secondary/50">{item.subLabel}</span>}
        </div>
      </div>
    </DropdownMenu.Item>
  );
};

const NavbarDropdownMenuItems = ({ items, onClose }: { items: NavbarItem[]; onClose: () => void }) => {
  return (
    <NavbarDropdownMenuCloseProvider closeMenu={onClose}>
      {items.map((item) => (
        <NavbarDropdownMenuItem key={item.label} item={item} />
      ))}
    </NavbarDropdownMenuCloseProvider>
  );
};

const NavbarDropdownMenu = ({ items }: { items: NavbarItem[] }) => {
  const contentPortalContainerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleClose = () => setOpen(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger
        className={
          "flex relative group m-2 px-2 gap-x-2 py-1! items-center lg:hidden cursor-pointer rounded-lg text-blue-grey-950 hover:bg-neutral-100"
        }
        data-testid={"navbar-menu-trigger"}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
      >
        <Icon icon="icon-lt-list-check" size="lg" cursor="pointer" />
        <span className="text-sm">{t("nav.nav.menu")}</span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal container={contentPortalContainerRef.current ?? undefined}>
        <DropdownMenu.Content
          side="bottom"
          sideOffset={5}
          align="start"
          alignOffset={0}
          className={clsx(
            "z-50 flex flex-col py-1 overflow-hidden bg-white border rounded-lg shadow-md w-60 border-blue-grey-200"
          )}
          data-testid={"navbar-menu-content"}
        >
          <NavbarDropdownMenuItems items={items} onClose={handleClose} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export const NavbarMenu = ({
  handleMapNavClick,
  handleSitesNavClick,
  handleProjectsNavClick,
}: {
  handleMapNavClick: () => void;
  handleSitesNavClick: () => void;
  handleProjectsNavClick: () => void;
}) => {
  const { t } = useTranslation();
  const { activeTab } = useActiveNavTab();
  const hasProjectsBetaFeature = useBetaFeatureFlag(Feature.projectsBeta);

  const NAVBAR_ITEMS: NavbarItem[] = [
    {
      id: "map",
      icon: "lt-road-map-line",
      label: t("nav.nav.map"),
      subLabel: "Explore the map and find sites",
      onClick: handleMapNavClick,
    },
    {
      id: "sites",
      icon: "lt-bar-graph-3",
      label: t("nav.nav.sites"),
      subLabel: "Manage your sites",
      onClick: handleSitesNavClick,
    },
    ...(hasProjectsBetaFeature
      ? [
          {
            id: "projects",
            icon: "lt-house-list-checkmark",
            label: t("nav.nav.projects"),
            subLabel: "Progress to your projects",
            onClick: handleProjectsNavClick,
          } satisfies NavbarItem,
        ]
      : []),
  ];

  return (
    <>
      <AnimatedSection
        show
        sectionKey="navbar-map-sites"
        className="px-4 flex-row gap-x-2 flex-nowrap hidden lg:flex"
        slidingDirection="left"
      >
        {NAVBAR_ITEMS.map((item) => (
          <Navbar.Item
            key={item.id}
            data-testid={`navbar-${item.id}`}
            active={activeTab === item.id}
            onClick={item.onClick}
            leadIcon={item.icon}
            leadIconProps={{
              alwaysShow: true,
              noPaddings: true,
            }}
          >
            {t(`nav.nav.${item.id}`)}
          </Navbar.Item>
        ))}
      </AnimatedSection>
      <NavbarDropdownMenuCloseProvider closeMenu={() => {}}>
        <NavbarDropdownMenu items={NAVBAR_ITEMS} />
      </NavbarDropdownMenuCloseProvider>
    </>
  );
};
